import React, { useEffect, useRef } from 'react';
import { useInfiniteHits, UseInfiniteHitsProps } from "react-instantsearch";
import { LeumitDoctor } from "assets/constants";
import { BaseHit, Hit } from "instantsearch.js";
import strings from 'assets/strings';
import { LeumitDoctorType, MashlimDoctorType } from "types";
import { MashlimDoctorResult } from "./mashlimDoctorResult/MashlimDoctorResult";
import LeumitDoctorResult from "./leumitDoctorResult/LeumitDoctorResult";

export default function InfiniteHitsComponent(props: UseInfiniteHitsProps) {
  const { hits, showMore, isLastPage } = useInfiniteHits(props);
  const hitsContainerRef = useRef<HTMLDivElement>(null);
  const firstNewResultRef = useRef<HTMLDivElement | null>(null);
  const prevHitsCount = useRef(hits.length);
  const isLoadMoreClicked = useRef(false);

  useEffect(() => {
    // Only focus if the load more button has been clicked
    // and new hits have been loaded
    if (isLoadMoreClicked.current && hits.length > prevHitsCount.current) {
      firstNewResultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      firstNewResultRef.current?.focus();
      isLoadMoreClicked.current = false; // Reset the flag
    }
    
    prevHitsCount.current = hits.length;
  }, [hits]);

  const handleShowMore = () => {
    isLoadMoreClicked.current = true; // Set flag when load more is clicked
    showMore();
  };

  return (
    <div className='animate' ref={hitsContainerRef}>
      {hits.map((hit: Hit<BaseHit>, index) => {
        // Only set ref and tabIndex for the first item of a new batch
        // and only when load more was clicked
        const isNewFirstHit = index === prevHitsCount.current && prevHitsCount.current > 0;
        
        return hit.type === LeumitDoctor ? (
          <LeumitDoctorResult
            hit={hit as LeumitDoctorType}
            key={`${hit.objectID}_${index}`}
            ref={isNewFirstHit ? firstNewResultRef : null}
            tabIndex={isNewFirstHit ? 0 : -1} 
          />
        ) : (
          <MashlimDoctorResult 
            hit={hit as MashlimDoctorType} 
            key={`${hit.objectID}_${index}`}
            ref={isNewFirstHit ? firstNewResultRef : null}
            tabIndex={isNewFirstHit ? 0 : -1}
          />
        );
      })}
      {!isLastPage && (
        <button
          className='ais-InfiniteHits-loadMore'
          onClick={handleShowMore}
          disabled={isLastPage}
        >
          {strings.globalStrings.moreResults}
        </button>
      )}
    </div>
  );
}
